import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, EMPTY, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { Http } from '../services/http';
import { Router } from '@angular/router';
import { LoginApiResponse } from '../models/user.model';
import { ErrorService } from '../services/error.service';

export const tokenRefreshInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // ✅ All inject() calls MUST be at the top level of the interceptor function,
  //    never inside RxJS operator callbacks (catchError, switchMap, etc.) — NG0203.
  const authService = inject(AuthService);
  const router = inject(Router);
  const http = inject(Http);
  const errorService = inject(ErrorService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only attempt refresh on 401, not on the refresh endpoint itself,
      // and not for login requests (no point refreshing if we're already logging in)
      if (
        error.status !== 401 ||
        req.url.includes('/auth/refresh') ||
        req.url.includes('/auth/login')
      ) {
        return throwError(() => error);
      }

      const refreshToken = authService.getRefreshToken();
      if (!refreshToken) {
        // No refresh token stored — session is gone, redirect immediately.
        // Return EMPTY so component error handlers don't fire a second toast.
        authService.logout();
        router.navigate(['/login']);
        return EMPTY;
      }

      // Backend expects: { refreshToken: string }
      // Backend returns: { status, message, result: { accessToken, refreshToken, tokenType, expiresIn } }
      return http.post<LoginApiResponse>('/auth/refresh', { refreshToken }).pipe(
        switchMap((response) => {
          const { accessToken, refreshToken: newRefreshToken } = response.result;
          authService.setTokens(accessToken, newRefreshToken);

          // Retry the original failed request with the new access token
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${accessToken}` },
          });
          return next(retried);
        }),
        catchError((refreshError) => {
          // Refresh token itself is expired or rejected — session cannot be recovered.
          // Show a dismissible warning toast so the user understands why they're being
          // redirected, then clear session and go to login.
          // Return EMPTY (not throwError) so component error handlers don't fire a
          // duplicate toast — the interceptor has already handled it.
          errorService.addError(
            'Session Expired',
            'Your session has expired. Please log in again.',
            'warning',
          );
          authService.logout();
          router.navigate(['/login']);
          return EMPTY;
        }),
      );
    }),
  );
};
